import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import Swal from "sweetalert2";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  SegmentGroup,
  Confirm,
  CardHeader,
  CardContent,
  Card,
  Icon,
  Loader,
  Dimmer,
  Form,
  Popup,
  Input,
} from "semantic-ui-react";
import { size } from "lodash";
import { Session, Week } from "../../../../../api";
import { useAuth } from "../../../../../hooks";
import { SessionItem } from "./SessionItem";
import "./Sidebar.scss";

const sessionController = new Session();
const weekController = new Week();

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

export function Sidebar(props) {
  const {
    weeks,
    weeksOptions,
    setWeeksOptions,
    subject,
    grade,
    syllabu,
    reload,
    onReload,
    onReloadWeek,
    activeSession,
    setActiveSessionEdit,
  } = props;
  const [loadingButtonId, setLoadingButtonId] = useState(null);
  const [weekToDelete, setWeekToDelete] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { accessToken } = useAuth();
  const sensors = useSensors(useSensor(PointerSensor));

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Handles the end of a drag-and-drop operation for session items within a sidebar component.
   *
   * This function is designed to work with draggable session elements, allowing users to reorder sessions
   * within a week or select a session to view its content. It distinguishes between two main actions based
   * on whether the dragged item (`active`) is dropped onto a different target (`over`) or not.
   *
   * Reordering Sessions:
   * - If the `active` session is dropped onto a different session (`over`), it identifies the week of the
   *   `active` session and calculates the new order of sessions.
   * - It then updates the sessions' order in the backend via `sessionController.updateIndexWeekSessions`.
   * - Upon successful reordering, it displays a success message using `Toast.fire` and triggers a UI reload
   *   through `onReload`.
   * - If an error occurs during the update, it logs the error to the console.
   *
   * Selecting a Session:
   * - If the `active` session is dropped onto itself (`active.id === over.id`), it checks the current `activeSession`.
   * - If no session is currently active (`activeSession.id === undefined`), it fetches the session's content using
   *   `sessionController.getSessionWithSlides` and sets it as the active session for editing.
   * - If the `active` session is already the active session (`activeSession.id === active.id`), it displays an info
   *   message indicating the content is already being viewed.
   * - If another session's content is currently being viewed, it displays a warning message advising the user to close
   *   the current session's content before viewing another.
   *
   * @param {Object} event - The event object containing details about the drag-and-drop operation.
   */
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const activeWeek = weeks.find((week) => {
        return week.sessions.find((session) => session.id === active.id);
      });
      const indexWeek = weeks.findIndex((week) => week.id === activeWeek.id);
      const activeIndex = activeWeek.sessions.findIndex(
        (session) => session.id === active?.id
      );
      const overIndex = activeWeek.sessions.findIndex(
        (session) => session?.id === over?.id
      );
      const newOrderSessions = arrayMove(
        activeWeek.sessions,
        activeIndex,
        overIndex
      );
      activeWeek.sessions = newOrderSessions;
      try {
        const data = {
          sessions: newOrderSessions,
        };
        await sessionController.updateIndexWeekSessions(accessToken, data);
        Toast.fire({
          icon: "success",
          title: `Se ordenaron con éxito las sesiones de la unidad ${
            indexWeek + 1
          }: ${activeWeek.title}.`,
        });
        onReload();
      } catch (error) {
        console.error(error);
      }
    } else {
      if (activeSession.id === undefined) {
        const response = await sessionController.getSessionWithSlides(
          accessToken,
          active.id
        );
        setActiveSessionEdit(response);
        Toast.fire({
          icon: "info",
          title: `Mostrando contenido de la sesión ${response.title}.`,
        });
      } else if (activeSession.id === active.id) {
        Toast.fire({
          icon: "info",
          title: `Ya estás visualizando el contenido de esta sesión.`,
        });
      } else {
        Toast.fire({
          icon: "warning",
          title: `Cierra el contenido de la sesión para visualizar otra.`,
        });
      }
    }
  };

  /**
   * Asynchronously updates the title of a specified week.
   *
   * This function is responsible for updating the title of a week based on user input. It first checks if the input value is empty,
   * displaying a warning message if so, and aborts the update process. If the input value is not empty, it trims the input, updates
   * the local state to reflect the new title in the UI, and sends a request to update the week's title on the server.
   *
   * Steps:
   * 1. Checks for empty input value. If empty, displays a warning and updates the local state to revert any changes made by the user in the UI.
   * 2. Maps over the `weeksOptions` state to update the title of the week that matches the given week's ID.
   * 3. Sets the loading state for the button associated with the week being updated to provide visual feedback.
   * 4. Updates the week's title in the local state.
   * 5. Sends an update request to the server with the new title.
   * 6. On success, displays a success message indicating the week has been renamed.
   * 7. Calls `onReloadWeek` to refresh the week's data.
   * 8. Catches any errors and logs them to the console.
   * 9. Finally, resets the loading state for the button.
   *
   * @param {Object} week - The week object containing details about the week to be updated.
   * @param {string} inputValue - The new title input by the user for the week.
   */
  const handleSaveTitleWeek = async (week, inputValue) => {
    try {
      if (!inputValue) {
        Toast.fire({
          icon: "warning",
          title: `El título de la unidad ${
            week.index + 1
          } no puede estar vacío.`,
        });
        const updatedWeeksOptions = weeks.map((option) =>
          option.value === week.id ? { ...option, text: week.title } : option
        );
        setWeeksOptions(updatedWeeksOptions);
        return;
      }
      const updatedWeeksOptions = weeksOptions.map((option) =>
        option.value === week.id ? { ...option, text: inputValue } : option
      );
      setWeeksOptions(updatedWeeksOptions);
      inputValue = inputValue.trim();
      setLoadingButtonId(week.id);
      week.title = inputValue;
      const data = { title: inputValue };
      await weekController.updateWeek(accessToken, week.id, data);
      Toast.fire({
        icon: "success",
        title: `Renombraste con éxito la unidad ${
          week.index + 1
        }: ${inputValue}.`,
      });
      onReloadWeek();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingButtonId(null);
    }
  };

  /**
   * Asynchronously deletes a specified week and updates the index of remaining weeks.
   *
   * This function is responsible for deleting a week from the list of weeks. It first finds the index of the week to be deleted
   * in the `weeks` array. If the week is found, it removes the week from the array and then updates the index of each remaining
   * week to reflect their new positions in the array. After updating the local `weeks` array, it sends a request to update the
   * index of all weeks on the server to ensure consistency. Following the index update, it sends another request to delete the
   * specified week from the server. Upon successful deletion, it displays a success message indicating the week has been
   * successfully deleted. Finally, it calls `onReloadWeek` to refresh the list of weeks.
   *
   * Steps:
   * 1. Finds the index of the week to be deleted in the `weeks` array.
   * 2. Removes the week from the `weeks` array if found.
   * 3. Updates the index of each remaining week in the array to reflect their new order.
   * 4. Sends a request to update the index of all weeks on the server.
   * 5. Sends a request to delete the specified week from the server.
   * 6. Displays a success message indicating the week has been successfully deleted.
   * 7. Calls `onReloadWeek` to refresh the list of weeks.
   *
   * @param {Object} week - The week object containing details about the week to be deleted.
   */
  const handleDeleteWeek = async (week) => {
    try {
      const indexWeek = weeks.findIndex(
        (weekData) => weekData.id === week.id
      );
      if (indexWeek > -1) {
        weeks.splice(indexWeek, 1);
        weeks.forEach((week, newIndex) => {
          week.index = newIndex;
        });
      }
      const data = {
        weeks: weeks,
      };
      await weekController.updateIndexWeeks(accessToken, data);
      await weekController.deleteWeek(accessToken, week.id);
      Toast.fire({
        icon: "success",
        title: `Eliminaste con éxito la unidad ${week.index + 1}: ${
          week.title
        }.`,
      });
      onReloadWeek();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Renders a container for managing a week's title and sessions in the sidebar.
   *
   * The `WeekContainer` component displays a form allowing the user to edit the week's title and provides options to save the new title,
   * delete the week, and view the sessions associated with the week. It uses the `useDroppable` hook from `dnd-kit` to enable drag-and-drop
   * functionality, allowing the week container to act as a droppable area.
   *
   * Features:
   * - An input field pre-filled with the week's current title, which can be edited by the user.
   * - A save button with a popup tooltip for saving the edited title. Clicking this button triggers the `handleSaveTitleWeek` function with the new title.
   * - A delete button with a popup tooltip for deleting the week. The button is only enabled if the week has no sessions. Clicking this button triggers a confirmation dialog.
   * - A list of sessions for the week, displayed using `SessionItem` components. If there are no sessions, a message indicating "No hay sesiones" is shown.
   *
   * The component also handles input changes by updating the `inputValue` state, ensuring the input value does not start with whitespace.
   *
   * @param {Object} week - The week object containing details such as the title, index, and sessions.
   */
  const WeekContainer = ({ week }) => {
    const { setNodeRef } = useDroppable({ id: `week-${week.id}` });
    const [inputValue, setInputValue] = useState(week.title);

    const handleChange = (event) => {
      setInputValue(event.target.value);
    };

    return (
      <div ref={setNodeRef} className="week-container">
        <Form>
          <Input action className="input-group">
            <Form.Input
              fluid
              className="input-title-week"
              label={"Unidad " + (week.index + 1)}
              placeholder={week.title}
              name="title"
              onChange={handleChange}
              value={inputValue.trimStart()}
            />
            <Popup
              content={`Renombrar unidad ${week.index + 1}`}
              trigger={
                <Form.Button
                  fluid
                  className="save-title-week"
                  type="button"
                  icon="save"
                  color="green"
                  loading={loadingButtonId === week.id}
                  onClick={() => handleSaveTitleWeek(week, inputValue)}
                />
              }
            />
            <Popup
              content={`Eliminar unidad ${week.index + 1}`}
              trigger={
                <Form.Button
                  fluid
                  className="delete-week"
                  type="button"
                  icon="trash alternate outline"
                  color="orange"
                  onClick={() => {
                    if (week.sessions.length !== 0) {
                      Toast.fire({
                        icon: "warning",
                        title: `No puedes eliminar la unidad ${
                          week.index + 1
                        }: ${week.title}, porque tiene sesiones.`,
                      });
                      return;
                    }
                    setWeekToDelete(week);
                    onOpenCloseConfirm(); // Asumiendo que [`showConfirm`](command:_github.copilot.openSymbolFromReferences?%5B%7B%22%24mid%22%3A1%2C%22fsPath%22%3A%22%2FUsers%2Fmac-luis%2FDesktop%2Fcurso-react%2FProyecto%20Edunova%2Fedunova-frontend%2Fsrc%2Fcomponents%2FAdmin%2FMedia%2FMediaEdit%2FSidebar%2FSidebar.js%22%2C%22external%22%3A%22file%3A%2F%2F%2FUsers%2Fmac-luis%2FDesktop%2Fcurso-react%2FProyecto%2520Edunova%2Fedunova-frontend%2Fsrc%2Fcomponents%2FAdmin%2FMedia%2FMediaEdit%2FSidebar%2FSidebar.js%22%2C%22path%22%3A%22%2FUsers%2Fmac-luis%2FDesktop%2Fcurso-react%2FProyecto%20Edunova%2Fedunova-frontend%2Fsrc%2Fcomponents%2FAdmin%2FMedia%2FMediaEdit%2FSidebar%2FSidebar.js%22%2C%22scheme%22%3A%22file%22%7D%2C%7B%22line%22%3A61%2C%22character%22%3A2%7D%5D "src/components/Admin/Media/MediaEdit/Sidebar/Sidebar.js") controla la visibilidad del diálogo de confirmación
                  }}
                />
              }
            />
          </Input>
        </Form>
        {size(week.sessions) !== 0 ? (
          <SegmentGroup>
            {week.sessions.map((session) => (
              <SessionItem
                key={session.id}
                session={session}
                activeSession={activeSession}
                onReload={onReload}
              />
            ))}
          </SegmentGroup>
        ) : (
          <div className="no-sessions">No hay sesiones</div>
        )}
      </div>
    );
  };

  return (
    <div className="sidebar-edit">
      <Card fluid>
        <CardContent className="content-header">
          <CardHeader>
            {subject == null && grade == null ? (
              <Loader active inline="centered" size="small" />
            ) : (
              <div>
                <Icon name="graduation" />
                {syllabu?.name || 'N/A'} - {grade?.name || 'N/A'}
                <div>
                  <Icon name="book" />
                  {subject?.name || 'N/A'}
                </div>
              </div>
            )}
          </CardHeader>
        </CardContent>
        <CardContent className="content-list">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            {size(weeks) === 0 && !reload ? (
              <h1>No existen unidads</h1>
            ) : size(weeks) === 0 || reload ? (
              <Dimmer active inverted>
                <Loader content="Cargando sesiones" />
              </Dimmer>
            ) : (
              weeks.map((weekData) => (
                <SortableContext
                  key={weekData.id}
                  items={weekData.sessions.map((session) => session.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <WeekContainer week={weekData} key={weekData.id} />
                </SortableContext>
              ))
            )}
          </DndContext>
        </CardContent>
      </Card>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={() => {
          onOpenCloseConfirm();
          setWeekToDelete(null); // Limpiar la unidad seleccionada al cancelar
        }}
        onConfirm={() => {
          if (weekToDelete) {
            handleDeleteWeek(weekToDelete);
            onOpenCloseConfirm(); // Ocultar el diálogo de confirmación después de confirmar
            setWeekToDelete(null); // Limpiar la unidad seleccionada después de eliminar
          }
        }}
        content={`¿Deseas eliminar la unidad?`}
        size="mini"
      />
    </div>
  );
}
